import { Base64 } from 'js-base64';
import { Platform } from 'react-native';

/**
 * Asks the browser to offer saving the credentials. The steps never submit a
 * real <form>, so chrome's own save heuristics miss first-time logins - this
 * tells it explicitly. Chromium-only API, a no-op everywhere else.
 */
export const saveBrowserCredentials = (id: string, password: string) => {
	const PasswordCredential = (window as any)?.PasswordCredential;
	if (Platform.OS !== 'web' || !password || !PasswordCredential) {
		return;
	}
	navigator.credentials
		?.store(new PasswordCredential({ id, password }))
		.catch(() => {}); // a refused save must never affect the login itself
};

/**
 * Israeli teudat zehut check-digit validation (standard Luhn-like algorithm):
 * pad to 9 digits, multiply alternately by 1/2, reduce two-digit products,
 * and the total must divide by 10.
 */
export const isValidIsraeliId = (id: string): boolean => {
	if (!/^\d{5,9}$/.test(id)) {
		return false;
	}
	const padded = id.padStart(9, '0');
	const sum = padded.split('').reduce((acc, char, index) => {
		const product = Number(char) * (index % 2 === 0 ? 1 : 2);
		return acc + (product > 9 ? product - 9 : product);
	}, 0);
	return sum % 10 === 0;
};

export const isStepTokenExpired = (token?: string | null): boolean => {
	if (!token) {
		return false;
	}
	try {
		// header.payload.signature - the middle part is the one with the claims
		const payload = JSON.parse(Base64.decode(token.split('.')[1]));
		// exp is in seconds, Date.now() in milliseconds
		return typeof payload?.exp === 'number' && payload.exp * 1000 <= Date.now();
	} catch {
		return false;
	}
};

/** where a code / reset link went, as the screens display it */
export interface OtpTarget {
	type: 'phone' | 'mail';
	/**
	 * Comes masked from the server - we only display it. Optional because a
	 * missing value must still leave the channel known: the texts have
	 * target-less variants for that.
	 */
	masked?: string;
}

/**
 * The bits of the profile the destination texts need. Structurally typed rather
 * than importing CheckIdResult so this file stays free of an import cycle with
 * SignInV2.api -> SignInV2.mock.
 */
interface OtpProfile {
	hasPhone: boolean;
	hasMail: boolean;
	/** already masked by the server - display only */
	phone?: string;
	mail?: string;
}

/**
 * Which destination the OTP goes to: phone first, mail as fallback. Decided by
 * what the employee has rather than by what we can display - the masked value
 * only enriches the text, so a missing one must not flip the channel.
 */
export const resolveOtpChannel = (
	profile: OtpProfile,
): 'phone' | 'mail' | undefined => {
	if (profile.hasPhone) {
		return 'phone';
	}
	if (profile.hasMail) {
		return 'mail';
	}
	return undefined;
};

/**
 * Dresses a channel the server already picked with the masked destination it
 * sent for that channel. An empty string is the same as nothing sent - the
 * texts then name the channel without a destination.
 */
export const toOtpTarget = (
	profile: OtpProfile | null | undefined,
	type: 'phone' | 'mail',
): OtpTarget => {
	const value = type === 'phone' ? profile?.phone : profile?.mail;
	// \u2066...\u2069 is an LTR isolate
	return { type, masked: value ? `\u2066${value}\u2069` : undefined };
};


export const resolveResetTarget = (profile?: OtpProfile | null): OtpTarget =>
	profile && !profile.hasMail && profile.hasPhone
		? toOtpTarget(profile, 'phone')
		: toOtpTarget(profile, 'mail');
